import type {
  Segment,
  SegmentBuildInput,
  SegmentFilters,
  SegmentGridSort,
  SegmentPortfolioSummary,
  SegmentPriorityClass,
  SegmentReport,
} from "../types"

import { mockSegmentReports, segmentMarkets } from "../data/mock-segment-analysis-data"

export function priorityClass(priority: string): SegmentPriorityClass {
  const lower = priority.toLowerCase()

  if (/(strategic core|high strategic|accelerate|high volume core)/.test(lower)) return "strat"
  if (/(growth|emerging|innovation)/.test(lower)) return "grow"
  if (/(defend|platform|stable)/.test(lower)) return "defend"
  return "other"
}

export function priorityStyle(priority: string): { bg: string; fg: string } {
  const styles: Record<SegmentPriorityClass, { bg: string; fg: string }> = {
    strat: { bg: "hsl(var(--chart-2))", fg: "hsl(var(--primary-foreground))" },
    grow: { bg: "hsl(var(--success))", fg: "hsl(var(--primary-foreground))" },
    defend: { bg: "hsl(var(--chart-1))", fg: "hsl(var(--primary-foreground))" },
    other: { bg: "hsl(var(--chart-4))", fg: "hsl(var(--primary-foreground))" },
  }

  return styles[priorityClass(priority)]
}

/**
 * Compact form of an addressable-line label for tight stat cells — "Addressable
 * Plastics" → "Addr. Plastics". Tenant-agnostic (just abbreviates the word
 * "Addressable" if present); leaves any other label untouched.
 */
export function shortAddressableLabel(label: string): string {
  return label.replace(/^Addressable\b/i, "Addr.")
}

/** Sum of a segment's per-line addressable values (MEUR). Shared by cards/charts/export/detail. */
export function segmentAddressableTotal(segment: Segment): number {
  return Object.values(segment.addressable ?? {}).reduce((sum, value) => sum + (value || 0), 0)
}

export function pseudoSeries(seed: number, trend: number, count = 12): number[] {
  let x = seed
  const out: number[] = []
  let value = 100

  for (let i = 0; i < count; i += 1) {
    x = (x * 9301 + 49297) % 233280
    const noise = (x / 233280 - 0.5) * 8
    value = value * (1 + trend / 100 / 12) + noise
    out.push(Math.round(value * 10) / 10)
  }

  return out
}

export function filterSegments(
  segments: Segment[],
  filters: SegmentFilters,
  currentUserOid?: string | null
): Segment[] {
  const search = filters.search.trim().toLowerCase()

  return segments.filter((segment) => {
    if (filters.market.length > 0 && !filters.market.includes(segment.marketName)) return false
    // Priority filter UI was removed (#2421); ignore orphaned sticky/preset values.
    if (filters.owner.length > 0 && !filters.owner.includes(segment.owner)) return false
    if (
      filters.favouritesOnly &&
      !(currentUserOid && segment.favouriteBy?.includes(currentUserOid))
    ) {
      return false
    }

    if (!search) return true

    const searchable = [
      segment.name,
      segment.marketName,
      segment.owner,
      segment.priority,
      ...segment.customers,
      ...segment.competitors,
    ]
      .join(" ")
      .toLowerCase()

    return searchable.includes(search)
  })
}

/**
 * Portfolio grid sort (#2068 B3). "own-first" reproduces the classic order
 * (visibleSegments already arrives own-first sorted, so it passes through);
 * the others are stable value sorts over the same list.
 */
export function sortSegmentsForGrid(segments: Segment[], sort: SegmentGridSort): Segment[] {
  if (sort === "own-first") return segments
  const sorted = [...segments]
  if (sort === "size") sorted.sort((a, b) => b.size - a.size)
  else if (sort === "growth") sorted.sort((a, b) => b.cagr - a.cagr)
  else sorted.sort((a, b) => a.name.localeCompare(b.name))
  return sorted
}

export function sortOwnedFirst(segments: Segment[], currentUserOid?: string | null): Segment[] {
  if (!currentUserOid) return segments
  // Stable sort: the caller's own segments float to the top, order otherwise kept (§5.7).
  return segments
    .map((segment, index) => ({ segment, index }))
    .sort((a, b) => {
      const aOwn = a.segment.ownerOid === currentUserOid ? 0 : 1
      const bOwn = b.segment.ownerOid === currentUserOid ? 0 : 1
      return aOwn - bOwn || a.index - b.index
    })
    .map((entry) => entry.segment)
}

/**
 * Per-segment ownership for owner-only writes (manual sales / addressable).
 * Matches backend ``is_owner_of``: prefer ``ownerOid``, else display-name match
 * for legacy/seed segments. Module admins are not owners unless they match.
 */
export function isSegmentOwner(
  segment: Pick<Segment, "owner" | "ownerOid">,
  currentUserOid?: string | null,
  currentUserName?: string | null
): boolean {
  if (segment.ownerOid) {
    return Boolean(currentUserOid && segment.ownerOid === currentUserOid)
  }
  return Boolean(segment.owner && currentUserName && segment.owner === currentUserName)
}

export function getOwnerNames(segments: Segment[]): string[] {
  const demoOwner = "Luca Moretti"
  return Array.from(new Set(segments.map((segment) => segment.owner))).sort((a, b) => {
    if (a === demoOwner) return -1
    if (b === demoOwner) return 1
    return a.localeCompare(b)
  })
}

export function getPortfolioSummary(segments: Segment[]): SegmentPortfolioSummary {
  const segmentCount = segments.length
  const totalSales = segments.reduce((sum, segment) => sum + segment.sales, 0)
  const totalMarket = segments.reduce((sum, segment) => sum + segment.size, 0)
  // Weighted by market size so the KPI sublabel ("weighted by market revenue")
  // is true — a small fast-growing niche shouldn't skew the portfolio CAGR the
  // way a plain mean would (#2066). Falls back to a plain mean when no segment
  // carries a size.
  const plainMeanGrowth =
    segmentCount > 0 ? segments.reduce((sum, segment) => sum + segment.cagr, 0) / segmentCount : 0
  const averageGrowth =
    totalMarket > 0
      ? segments.reduce((sum, segment) => sum + segment.cagr * segment.size, 0) / totalMarket
      : plainMeanGrowth
  const averageFit =
    segmentCount > 0 ? segments.reduce((sum, segment) => sum + segment.fit, 0) / segmentCount : 0

  // Sum each addressable line by key across whatever segments carry the data. The
  // component renders the tenant-configured lines from this; lines with no data
  // sum to absent, which drives the honest "generic Addressable market" fallback.
  const addressableTotals: Record<string, number> = {}
  for (const segment of segments) {
    if (!segment.addressable) continue
    for (const [key, value] of Object.entries(segment.addressable)) {
      if (typeof value === "number" && Number.isFinite(value)) {
        addressableTotals[key] = (addressableTotals[key] ?? 0) + value
      }
    }
  }

  return {
    segmentCount,
    totalSales,
    totalMarket,
    averageGrowth: Math.round(averageGrowth * 10) / 10,
    averageFit: Math.round(averageFit * 10) / 10,
    addressableTotals,
  }
}

export function buildSegment(input: SegmentBuildInput): Segment {
  const market =
    segmentMarkets.find((item) => item.marketName === input.marketName) ?? segmentMarkets[0]
  const seed = input.name.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0)

  return {
    id: input.id,
    name: input.name || "Untitled segment",
    marketName: market.marketName,
    strategicRole: market.strategicRole,
    description: input.description || "User-created segment pending baseline analysis.",
    owner: input.owner,
    // No default priority (#3295 / Nolato feedback #19): the field is retired.
    ...(input.priority ? { priority: input.priority } : {}),
    size: 200,
    cagr: 6,
    sales: 4,
    share: 2,
    customers: input.customers,
    suppliers: [],
    competitors: input.competitors,
    attractiveness: 6.5,
    fit: 6,
    sparkline: pseudoSeries(seed, 6),
    lastRunDate: "2026-05-07",
    runStatus: "ok",
  }
}

export function getSegmentReport(segmentId: string | null): SegmentReport | null {
  if (!segmentId) return null
  return mockSegmentReports[segmentId] ?? null
}

export function consolidatedTrend(segments: Segment[]): number[] {
  const count = 12
  const totals = new Array<number>(count).fill(0)

  for (const segment of segments) {
    const last = segment.sparkline.at(-1) || 1
    segment.sparkline.forEach((point, index) => {
      totals[index] += (point / last) * segment.sales
    })
  }

  return totals.map((point) => Math.round(point * 10) / 10)
}

/** Collapse punctuation / legal suffixes so "De'Longhi Group" matches "De'Longhi". */
export function normalizeCompanyName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/['’`´]/g, "")
    .replace(/\b(group|inc|ltd|llc|ab|oy|gmbh|corp|corporation|co)\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
}

/** True when every whitespace-separated token in `needle` appears as a whole
 * token in `haystack` — e.g. "delonghi" ⊂ "delonghi sweden", but "apple" is
 * NOT ⊂ "snapple" (no shared whole token), unlike a raw substring check. */
function tokensContained(haystack: string, needleTokens: string[]): boolean {
  const haystackTokens = haystack.split(" ").filter(Boolean)
  return needleTokens.length > 0 && needleTokens.every((token) => haystackTokens.includes(token))
}

/** Resolve a display name to a registry company (exact, then normalized, then
 * whole-token containment — never a raw substring match, which would let
 * unrelated names like "Snapple" resolve to "Apple"). */
export function matchCompanyByName<T extends { name: string }>(
  companies: T[],
  name: string
): T | undefined {
  const exact = companies.find((company) => company.name.toLowerCase() === name.toLowerCase())
  if (exact) return exact
  const needle = normalizeCompanyName(name)
  if (!needle) return undefined
  const needleTokens = needle.split(" ").filter(Boolean)
  return (
    companies.find((company) => normalizeCompanyName(company.name) === needle) ??
    companies.find((company) => {
      const candidate = normalizeCompanyName(company.name)
      const candidateTokens = candidate.split(" ").filter(Boolean)
      return tokensContained(candidate, needleTokens) || tokensContained(needle, candidateTokens)
    })
  )
}
